import {useCallback, useEffect, useRef, useState} from 'react';

// Qué escaneo de solapas explica la pantalla cuando no hay propuesta.
//
// Hay dos fuentes y compiten: el que devuelve "leer el plan" (más fresco, pero sólo existe si el
// usuario acaba de releer) y el que viaja con la conexión. La regla es simple pero se rompe fácil:
// el local gana **hasta que llega uno nuevo por la conexión**. Sin esa invalidación, fijar una
// solapa —que relee por otro camino, el del strip— dejaba la pantalla explicando el motivo del
// intento anterior: la pantalla que existe para decir la verdad, mintiendo.
//
// Vive en su propio módulo para que el invariante se pueda testear ejerciendo ESTE código y no una
// réplica: un guardián que copia la regla en el test sigue verde el día que alguien borra la regla.

/**
 * @param {object|null} connectionScan escaneo que viaja con la conexión (prop)
 * @returns {{scan, repeatedFailure, recordScan, reset}}
 */
export function useScanFreshness(connectionScan) {
    const [localScan, setLocalScan] = useState(null);
    const [repeatedFailure, setRepeatedFailure] = useState(false);
    const lastFailureCode = useRef(null);

    const reset = useCallback(() => {
        setLocalScan(null);
        setRepeatedFailure(false);
        lastFailureCode.current = null;
    }, []);

    // Un escaneo nuevo por la conexión invalida al local: es más nuevo por definición (viene de una
    // lectura posterior). `repeatedFailure` cae con él, porque se calcula contra el intento previo.
    useEffect(() => {
        reset();
    }, [connectionScan, reset]);

    /**
     * Registra el escaneo que devolvió una relectura explícita y devuelve el motivo del descarte
     * (o null si hubo propuesta), para que el caller decida el aviso.
     */
    const recordScan = useCallback((scan, hasProposal) => {
        if (scan) setLocalScan(scan);
        if (hasProposal) {
            setRepeatedFailure(false);
            lastFailureCode.current = null;
            return null;
        }
        const reason = ((scan || {}).reasons || [])[0] || null;
        const code = reason ? reason.code : null;
        // Dos intentos seguidos con el mismo motivo: releer no va a cambiar nada y hay que decirlo,
        // en vez de dejar al usuario clickeando.
        setRepeatedFailure(!!(code && lastFailureCode.current === code));
        lastFailureCode.current = code;
        return reason;
    }, []);

    return {scan: localScan || connectionScan, repeatedFailure, recordScan, reset};
}
